<div class="gantt rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden" [class.is-dragging]="!!dragState">

  <!-- Toolbar -->
  <div class="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100 bg-gray-50/60">
    <div class="flex items-center gap-3 text-xs text-gray-500">
      <span class="inline-flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded-full bg-slate-400"></span> New</span>
      <span class="inline-flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded-full bg-blue-500"></span> In Progress</span>
      <span class="inline-flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Done</span>
    </div>
    <div class="flex items-center gap-2">
      <button type="button" (click)="scrollToToday()"
        class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
        Today
      </button>
      <div class="inline-flex rounded-lg bg-gray-100 p-0.5">
        @for(z of ['day','week','month','quarter']; track z) {
        <button type="button" (click)="setZoom($any(z))"
          class="px-2.5 py-1 rounded-md text-xs font-semibold capitalize transition-colors cursor-pointer"
          [ngClass]="zoom === z ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'">{{ z }}</button>
        }
      </div>
    </div>
  </div>

  <!-- Body: tree (left) + timeline (right) -->
  <div class="flex">

    <!-- ── Left tree ── -->
    <div #treeScroll (scroll)="onTreeScroll()" [style.height.px]="chartHeight"
      class="tree-scroll shrink-0 w-[620px] border-r border-gray-200 overflow-y-auto overflow-x-hidden">

      <!-- Tree header -->
      <div class="tree-row sticky top-0 z-20 bg-gray-50 border-b border-gray-200 flex items-stretch text-[11px] font-bold text-gray-400 uppercase tracking-wider"
        [style.height.px]="HEADER_HEIGHT">
        <div class="flex items-center flex-1 min-w-[150px] px-3">Task</div>
        <div class="flex items-center w-[210px] px-2">Dates</div>
        <div class="flex items-center w-[66px] px-1">Hours</div>
        <div class="flex items-center w-[112px] px-1">Status</div>
        <div class="w-[52px]"></div>
      </div>

      <!-- Tree rows -->
      @for(row of rows; track trackRow($index, row)) {
      @let item = row.item;
      <div class="tree-row group flex items-stretch border-b border-gray-50 hover:bg-gray-50/70 transition-colors"
        [style.height.px]="ROW_HEIGHT" [style.background]="getRowTint(item)">

        <!-- Name -->
        <div class="flex items-center gap-1.5 flex-1 min-w-[150px] px-3" [style.paddingLeft.px]="12 + row.level * 16">
          @if(hasChildren(item)) {
          <button type="button" (click)="toggleExpand(item)" class="shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer">
            <svg class="w-3.5 h-3.5 transition-transform" [class.rotate-90]="item.expanded" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" /></svg>
          </button>
          } @else {
          <span class="shrink-0 w-4"></span>
          }

          @if(item.isStage || item.isProject) {
          <span class="relative shrink-0 w-3.5 h-3.5">
            <span class="absolute inset-0 rounded-full ring-2 ring-white shadow-sm" [style.background]="getColorFromStage(item)"></span>
            <input type="color" [value]="item.color || '#6366f1'" (click)="$event.stopPropagation()"
              (change)="onColorChange(item, $any($event.target).value)"
              class="absolute inset-0 opacity-0 cursor-pointer w-full h-full" title="Change color">
          </span>
          } @else {
          <span class="shrink-0 w-2 h-2 rounded-full" [style.background]="getColorFromStage(item)"></span>
          }

          @if(isEditing(item, 'name')) {
          <input type="text" [value]="item.title" autofocus
            (click)="$event.stopPropagation()"
            (keydown.enter)="$any($event.target).blur()"
            (keydown.escape)="editing = null"
            (blur)="commitName(item, $any($event.target).value)"
            class="flex-1 min-w-0 px-1.5 py-0.5 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-400 outline-none">
          } @else {
          @if(item.isProject) {
          <a [routerLink]="['/projects', item.realId]" class="truncate font-semibold text-sm text-gray-900 hover:text-blue-600 no-underline">{{ item.title }}</a>
          } @else {
          <span (click)="startEdit(item, 'name', $event)"
            class="truncate text-sm cursor-text hover:text-blue-700"
            [class.font-semibold]="item.isStage" [class.text-gray-800]="item.isStage" [class.text-gray-700]="!item.isStage"
            [style.color]="item.isStage ? item.color : null"
            title="Click to rename">{{ item.title }}</span>
          }
          }
        </div>

        <!-- Dates -->
        <div class="flex items-center gap-1 w-[210px] px-2">
          @if(!item.isProject) {
          <input type="date" [value]="toInput(item.start)" (change)="onDateChange(item, 'start', $any($event.target).value)"
            class="gantt-date w-[92px]">
          <span class="text-gray-300 text-xs">→</span>
          <input type="date" [value]="toInput(item.end)" (change)="onDateChange(item, 'end', $any($event.target).value)"
            class="gantt-date w-[92px]">
          } @else {
          <span class="text-xs text-gray-500 truncate">{{ item.start | date:'MMM d' }} – {{ item.end | date:'MMM d, y' }}</span>
          }
        </div>

        <!-- Hours -->
        <div class="flex items-center w-[66px] px-1">
          @if(item.isStage || item.isProject) {
          @let encoded = getEncodedHours(item);
          @let budget = item.estimatedHours || 0;
          <span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-bold cursor-text"
            (click)="startEdit(item, 'hours', $event)"
            [ngClass]="budget > 0 ? (budget >= encoded ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600') : 'bg-gray-50 text-gray-600'"
            [title]="'Encoded ' + (encoded | number:'1.0-1') + 'h / Budget ' + budget + 'h — click to edit budget'">
            @if(isEditing(item, 'hours')) {
            <input type="number" min="0" step="0.5" [value]="item.estimatedHours || 0" autofocus
              (click)="$event.stopPropagation()" (keydown.enter)="$any($event.target).blur()" (keydown.escape)="editing = null"
              (blur)="commitHours(item, $any($event.target).value)"
              class="w-12 px-1 text-[11px] border border-blue-300 rounded outline-none">
            } @else {
            {{ encoded | number:'1.0-1' }}<span class="text-gray-400 font-normal">/{{ budget }}h</span>
            }
          </span>
          } @else {
          @if(isEditing(item, 'hours')) {
          <input type="number" min="0" step="0.5" [value]="item.estimatedHours || 0" autofocus
            (click)="$event.stopPropagation()" (keydown.enter)="$any($event.target).blur()" (keydown.escape)="editing = null"
            (blur)="commitHours(item, $any($event.target).value)"
            class="w-12 px-1 text-[11px] border border-blue-300 rounded outline-none">
          } @else {
          <span class="text-xs font-medium text-gray-600 cursor-text" (click)="startEdit(item, 'hours', $event)" title="Click to edit">{{ item.estimatedHours || 0 }}h</span>
          }
          }
        </div>

        <!-- Status -->
        <div class="flex items-center w-[112px] px-1">
          @if(!item.isProject) {
          <div class="relative w-full">
            <select [value]="item.status" (change)="onStatusChange(item, $any($event.target).value)"
              class="w-full appearance-none pl-2 pr-6 py-1 rounded-md text-[11px] font-semibold border cursor-pointer outline-none focus:ring-2 focus:ring-blue-400/40"
              [ngClass]="getStatusClass(item.status)">
              @for(opt of statusOptions; track opt.value) { <option [value]="opt.value">{{ opt.text }}</option> }
            </select>
            <svg class="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" /></svg>
          </div>
          }
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-0.5 w-[52px] pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
          @if(item.isStage) {
          <button type="button" (click)="onAddTaskClick(item, $event)" title="Add task"
            class="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
          </button>
          } @else if(item.isProject) {
          <button type="button" (click)="onAddStageClick(item, $event)" title="Add phase"
            class="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
          </button>
          } @else {
          <button type="button" (click)="requestDelete(item, $event)" title="Delete task"
            class="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
          </button>
          }
        </div>
      </div>
      }
    </div>

    <!-- ── Right timeline ── -->
    <div #timeScroll (scroll)="onTimeScroll()" [style.height.px]="chartHeight"
      class="time-scroll flex-1 overflow-auto">
      <div class="relative" [style.width.px]="timelineWidth">

        <!-- Header -->
        <div class="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-200" [style.height.px]="HEADER_HEIGHT">
          <div class="relative border-b border-gray-100" [style.height.px]="HEADER_HEIGHT / 2">
            @for(seg of topSegments; track seg.key) {
            <div class="absolute inset-y-0 flex items-center px-2 text-[11px] font-bold text-gray-500 border-r border-gray-100 truncate"
              [style.left.px]="seg.left" [style.width.px]="seg.width">{{ seg.label }}</div>
            }
          </div>
          <div class="relative" [style.height.px]="HEADER_HEIGHT / 2">
            @for(seg of bottomSegments; track seg.key) {
            <div class="absolute inset-y-0 flex items-center justify-center text-[10px] text-gray-400 border-r border-gray-100"
              [style.left.px]="seg.left" [style.width.px]="seg.width">{{ seg.label }}</div>
            }
          </div>
        </div>

        <!-- Chart body -->
        <div class="relative">
          <!-- gridlines -->
          @for(seg of bottomSegments; track seg.key) {
          <div class="absolute top-0 bottom-0 border-l border-gray-100" [style.left.px]="seg.left"></div>
          }
          <!-- today -->
          @if(todayVisible) {
          <div class="absolute top-0 bottom-0 w-px bg-red-400 z-10" [style.left.px]="todayLeft">
            <span class="absolute -top-0.5 -left-1 w-2 h-2 rounded-full bg-red-400"></span>
          </div>
          }

          <!-- rows -->
          @for(row of rows; track trackRow($index, row)) {
          @let item = row.item;
          @let isSummary = item.isStage || item.isProject;
          <div class="relative border-b border-gray-50" [style.height.px]="ROW_HEIGHT" [style.background]="getRowTint(item)">
            <div class="gantt-bar absolute rounded-md flex items-center overflow-hidden select-none shadow-sm"
              [class.summary]="isSummary"
              [class.cursor-grab]="!item.isProject"
              [style.left.px]="barLeft(item)"
              [style.width.px]="barWidth(item)"
              [style.top.px]="isSummary ? 13 : 11"
              [style.height.px]="isSummary ? 18 : 22"
              [style.background]="getColorFromStage(item)"
              [title]="item.title"
              (pointerdown)="!item.isProject && startDrag(item, 'move', $event)">

              <!-- completion overlay -->
              @if(item.completionRatio > 0) {
              <div class="absolute inset-y-0 left-0 bg-black/20" [style.width.%]="item.completionRatio * 100"></div>
              }

              <span class="relative px-2 text-[11px] font-semibold text-white truncate pointer-events-none">{{ item.title }}</span>

              @if(!item.isProject) {
              <!-- resize handles -->
              <div class="gantt-handle left" (pointerdown)="startDrag(item, 'resize-start', $event)"></div>
              <div class="gantt-handle right" (pointerdown)="startDrag(item, 'resize-end', $event)"></div>
              }
            </div>
          </div>
          }

          @if(rows.length === 0) {
          <div class="flex items-center justify-center py-16 text-sm text-gray-400">No phases to display</div>
          }
        </div>
      </div>
    </div>
  </div>
</div>



@if (showConfirmation) {
<app-confirm-modal message="Are you sure you want to delete this task?" (close)="handleConfirm($event)"></app-confirm-modal>
}
